/**
 * Pure helpers for the data-policy masking dropdowns (default ELSE mask and
 * per-role block masks). Kept free of React/component imports so the option and
 * edit-rehydration logic can be unit-tested in isolation.
 */

export const DataPolicyMasks = {
  FullMask: 'FULL_MASK',
};

export interface MaskMethodOption {
  label: string;
  value: string;
}

// Masking methods that can never be the default (CASE ELSE) mask. NO_MASK would
// leave every unenumerated role unmasked, so the backend rejects it too - this
// keeps the UI fail-closed in step with that contract.
const NON_DEFAULT_MASK_METHODS = ['NO_MASK'];

// 'FULL_MASK' -> 'Full Mask', 'ANONYMIZATION' -> 'Anonymization'. Used only as a
// fallback label when the backend metadata has not (yet) advertised the method.
const humanizeMaskMethod = (method: string): string =>
  method
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

/**
 * The default (ELSE) mask is selectable among the non-raw methods the backend
 * advertises for blocks. Full Mask is always offered and kept first as the
 * fail-closed recommendation; NO_MASK is filtered out. Deriving from the same
 * `maskingOptions` the blocks use keeps the two dropdowns in lockstep.
 */
export const getDefaultMaskOptions = (
  maskingOptions?: MaskMethodOption[],
): MaskMethodOption[] => {
  const options = maskingOptions ?? [];
  const fullMask = options.find(o => o?.value === DataPolicyMasks.FullMask) ?? {
    label: humanizeMaskMethod(DataPolicyMasks.FullMask),
    value: DataPolicyMasks.FullMask,
  };
  const others = options.filter(
    o =>
      o?.value !== DataPolicyMasks.FullMask &&
      !NON_DEFAULT_MASK_METHODS.includes(o?.value),
  );
  return [fullMask, ...others];
};

/**
 * Resolve a stored default-mask method back to its {label, value} option for
 * display in edit mode, so a saved PARTIAL/ANON default renders with the right
 * label instead of always reading "Full Mask".
 */
export const resolveDefaultMaskOption = (
  method?: string,
  maskingOptions?: MaskMethodOption[],
): MaskMethodOption => {
  const value = method ?? DataPolicyMasks.FullMask;
  return (
    getDefaultMaskOptions(maskingOptions).find(o => o.value === value) ?? {
      label: humanizeMaskMethod(value),
      value,
    }
  );
};

/**
 * Role-level (block) mask options. Blocks keep every method the backend
 * advertises - including NO_MASK, since a matched role is often granted raw
 * access - and gain FULL_MASK so a role can be tightened above a weaker default.
 * FULL_MASK is appended last (NO_MASK stays first, the default for a new block).
 */
export const getBlockMaskOptions = (
  maskingOptions?: MaskMethodOption[],
): MaskMethodOption[] => {
  const options = maskingOptions ?? [];
  if (options.some(o => o?.value === DataPolicyMasks.FullMask)) return options;
  return [
    ...options,
    {
      label: humanizeMaskMethod(DataPolicyMasks.FullMask),
      value: DataPolicyMasks.FullMask,
    },
  ];
};

/**
 * Label the block option that matches the current default mask. Such a block
 * compiles to a CASE branch identical to the ELSE, so it changes nothing today
 * - but it stays selectable because it pins that role's mask if the default is
 * later relaxed.
 */
export const annotateBlockMaskOptions = (
  maskingOptions?: MaskMethodOption[],
  defaultMaskMethod?: string,
): MaskMethodOption[] =>
  (maskingOptions ?? []).map(option =>
    !!defaultMaskMethod && option?.value === defaultMaskMethod
      ? { ...option, label: `${option.label} (same as default)` }
      : option,
  );

/**
 * Resolve a stored block method to its {label, value} option for edit
 * rehydration. Synthesizes an option for a method the metadata does not
 * advertise (e.g. a FULL_MASK override) instead of silently downgrading it to
 * the first option - which would turn a fully-masked role into raw NO_MASK.
 * Returns undefined only when no method is stored, so callers can keep their own
 * fallback for that degenerate case.
 */
export const resolveBlockMaskOption = (
  method?: string,
  maskingOptions?: MaskMethodOption[],
): MaskMethodOption | undefined => {
  if (!method) return undefined;
  return (
    (maskingOptions ?? []).find(o => o?.value === method) ?? {
      label: humanizeMaskMethod(method),
      value: method,
    }
  );
};
